/* TSOVehicle — SITE-WIDE vehicle personalization layer (loaded from
   theme.liquid, defer). Shopify pages are CDN-cached, so vehicle copy MUST
   resolve client-side: one cookie reader, one vehicle:selection:updated
   subscription, one page pass. Any theme-editor field works — no per-section
   wiring needed:

   TEXT — in any text/richtext box, tokens fill from the saved truck:
     {vehicle}  {year}  {make}  {model}
     {vehicle|YOUR Truck}   <- pipe = fallback text when no vehicle is saved
   LINKS — in any URL box:
     /#garage          -> the saved truck's collection (garage_url);
                          opens the YMM drawer when no vehicle is saved
     /#select-vehicle  -> always opens the YMM drawer
   IMAGES — set an image's ALT text to {vehicle-image}:
     swaps to the saved truck's generation image when present; the picked
     image stays as the fallback. Alt becomes the vehicle name.

   Section-level API (used by tso-* sections; no raw-token flash):
     <tso-vehicle-text template="…{vehicle}…">generic fallback</tso-vehicle-text>
     data-tso-vehicle-show / data-tso-vehicle-hide
     data-tso-vehicle-garage-href   data-tso-vehicle-image

   Display-only cookie read — commitSelection() in the drawer owns all
   writes (THEME-1 contract). ponytail: full-page re-scan per vehicle change
   (rare event, ms-scale walk); per-node observers if that ever matters. */
(function () {
  'use strict';
  if (window.TSOVehicle) return;

  var TOKEN_RE = /\{(vehicle|year|make|model)(?:\|([^}]*))?\}/g;

  function read() {
    try {
      var m = document.cookie.match(/(?:^|; )vehicle_selection=([^;]*)/);
      var v = m ? JSON.parse(decodeURIComponent(m[1])) : null;
      // pre-rebuild cookies (before 2026-06-22) carry /pages/garage/* here;
      // those pages are unpublished (404) — treat as no garage link so the
      // fallback (open the YMM drawer) kicks in instead
      if (v && v.garage_url && v.garage_url.indexOf('/pages/garage') === 0) v.garage_url = null;
      return v;
    } catch (e) { return null; }
  }

  function tokenValue(v, key) {
    if (!v) return null;
    return (key === 'vehicle' ? v.vehicle_name : v[key]) || null;
  }

  function fill(template, v) {
    return template.replace(TOKEN_RE, function (_, key, fallback) {
      return tokenValue(v, key) || fallback || '';
    });
  }

  var subs = [];
  function notify() {
    var v = read();
    for (var i = 0; i < subs.length; i++) subs[i](v);
  }

  window.TSOVehicle = {
    get: read,
    fill: function (t, v) { return fill(t, v && v.vehicle_name ? v : null); },
    subscribe: function (fn) { subs.push(fn); fn(read()); }
  };

  document.addEventListener('vehicle:selection:updated', notify);

  if (!customElements.get('tso-vehicle-text')) {
    customElements.define('tso-vehicle-text', class extends HTMLElement {
      connectedCallback() {
        if (this._wired) return;
        this._wired = true;
        this._fallback = this.textContent;
        var self = this;
        window.TSOVehicle.subscribe(function (v) {
          var tpl = self.getAttribute('template');
          self.textContent = (v && v.vehicle_name && tpl) ? fill(tpl, v) : self._fallback;
        });
      }
    });
  }

  /* ---- global page pass ---- */
  var originals = new WeakMap(); // text node -> its authored template

  function scanTextNodes(v) {
    var hasVehicle = v && v.vehicle_name ? v : null;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
      acceptNode: function (n) {
        var s = originals.get(n) || n.nodeValue;
        if (s.indexOf('{') === -1) return NodeFilter.FILTER_REJECT;
        var p = n.parentElement;
        if (!p || p.closest('script,style,noscript,textarea,tso-vehicle-text')) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    });
    var nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(function (n) {
      var tpl = originals.get(n);
      if (tpl === undefined) {
        tpl = n.nodeValue;
        TOKEN_RE.lastIndex = 0; // global regex: reset before .test
        if (!TOKEN_RE.test(tpl)) return; // brace but no known token — leave alone
        originals.set(n, tpl);
      }
      n.nodeValue = fill(tpl, hasVehicle);
    });
  }

  function applyAttributes(v) {
    var has = !!(v && v.vehicle_name);
    document.querySelectorAll('[data-tso-vehicle-show]').forEach(function (el) { el.hidden = !has; });
    document.querySelectorAll('[data-tso-vehicle-hide]').forEach(function (el) { el.hidden = has; });
    document.querySelectorAll('[data-tso-vehicle-garage-href]').forEach(function (el) {
      if (has && v.garage_url) {
        el.setAttribute('href', v.garage_url);
        el.removeAttribute('data-tso-open-vehicle');
      }
    });
    document.querySelectorAll('[data-tso-vehicle-image]').forEach(function (el) {
      if (has && v.generation_image) {
        el.setAttribute('src', v.generation_image);
        el.removeAttribute('srcset');
        el.hidden = false;
      }
    });
    // URL-box conventions — work in ANY section's link picker
    document.querySelectorAll('a[href$="#garage"]').forEach(function (el) {
      if (has && v.garage_url) {
        el.setAttribute('href', v.garage_url);
        el.removeAttribute('data-tso-open-vehicle');
      } else {
        el.setAttribute('data-tso-open-vehicle', '');
      }
    });
    document.querySelectorAll('a[href$="#select-vehicle"]').forEach(function (el) {
      el.setAttribute('data-tso-open-vehicle', '');
    });
    // ALT-text convention — any image picker becomes vehicle-aware
    document.querySelectorAll('img[alt*="{vehicle-image}"], img[data-tso-veh-img-fallback]').forEach(function (el) {
      if (!el.hasAttribute('data-tso-veh-img-fallback')) {
        el.setAttribute('data-tso-veh-img-fallback', el.currentSrc || el.getAttribute('src') || '');
      }
      if (has && v.generation_image) {
        el.setAttribute('src', v.generation_image);
        el.removeAttribute('srcset');
        el.setAttribute('alt', v.vehicle_name);
      } else {
        var fb = el.getAttribute('data-tso-veh-img-fallback');
        if (fb && el.getAttribute('src') !== fb) { el.setAttribute('src', fb); }
        el.setAttribute('alt', '');
      }
    });
  }

  // drawer opener for the URL conventions (same contract as tso-vehicle-trigger)
  document.addEventListener('click', function (e) {
    var t = e.target.closest('a[href$="#garage"]:not([href^="/collections"]), a[href$="#select-vehicle"]');
    if (!t) return;
    var d = document.getElementById('vs-drawer');
    var o = document.getElementById('vs-overlay');
    if (!d || !o) return;
    e.preventDefault();
    d.hidden = false; o.hidden = false;
    requestAnimationFrame(function () {
      d.classList.add('is-open');
      o.classList.add('is-open');
    });
  });

  function applyAll(v) { scanTextNodes(v); applyAttributes(v); }

  /* Late DOM never went through the first pass: async section renders
     (related-products, tso-upsell-cards), quick-add, the cart drawer, builder
     popups — and, in the theme editor, EVERY section re-render after a settings
     edit, which is why an authored {vehicle} could look dead while you were
     editing the section. One debounced re-scan covers every injector without
     per-section wiring. childList only: our own writes are nodeValue/attribute
     edits (characterData/attributes), so this cannot re-trigger itself, and the
     WeakMap of authored templates makes the re-scan idempotent.
     ponytail: full re-walk per burst; diff only the added nodes if it ever costs. */
  var pending = 0;
  function watch() {
    if (!window.MutationObserver) return;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () { pending = 0; applyAll(read()); }, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function start() { subs.push(applyAll); applyAll(read()); watch(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
